/**
 * useDownloadEntityExport (P62.2, #481) -- admin mutation that GETs
 * the per-entity export endpoint and streams the response body as a
 * Blob, triggering a browser download via a hidden anchor.
 *
 * Mirrors the dossier + board-PDF download patterns
 * (`useDownloadDossier`, `useDownloadBoardPdf`) -- same Bearer token
 * source, same anchor-click pattern, same Content-Disposition parse.
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { currentAuthToken } from "shared/api/client";

export interface EntityExportInput {
  kind: string;
  entityId: string;
  format: "pdf" | "json";
  /** ISO 8601 with tz offset; defaults to NOW on the backend. */
  as_of?: string | undefined;
}

export interface EntityExportResult {
  filename: string;
}

export class EntityExportError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(detail);
    this.name = "EntityExportError";
  }
}

function _filenameFromHeaderOrFallback(
  header: string | null,
  input: EntityExportInput,
): string {
  if (header) {
    const match = header.match(/filename="?([^";]+)"?/);
    if (match?.[1]) return match[1];
  }
  return `entity-${input.kind}-${input.entityId}.${input.format}`;
}

async function _triggerDownload(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function _downloadEntityExport(
  input: EntityExportInput,
): Promise<EntityExportResult> {
  const token = currentAuthToken();
  const qs = input.as_of ? `?as_of=${encodeURIComponent(input.as_of)}` : "";
  const path =
    `/v1/admin/entities/${encodeURIComponent(input.kind)}` +
    `/${encodeURIComponent(input.entityId)}/export.${input.format}${qs}`;
  const response = await fetch(path, {
    method: "GET",
    headers: {
      Accept: input.format === "pdf" ? "application/pdf" : "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    let detail: string;
    try {
      const body = (await response.json()) as { detail?: string };
      detail = body?.detail ?? response.statusText;
    } catch {
      detail = response.statusText || `HTTP ${response.status}`;
    }
    throw new EntityExportError(response.status, detail);
  }

  const filename = _filenameFromHeaderOrFallback(
    response.headers.get("Content-Disposition"),
    input,
  );
  const blob = await response.blob();
  await _triggerDownload(blob, filename);
  return { filename };
}

export function useDownloadEntityExport(): UseMutationResult<
  EntityExportResult,
  EntityExportError,
  EntityExportInput
> {
  return useMutation({
    mutationFn: _downloadEntityExport,
  });
}
